import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePlatformAdmin } from '@/hooks/usePlatformAdmin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserPlus, UserMinus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

interface Admin {
  user_id: string;
  business_email: string;
  legal_first_name: string;
  legal_last_name: string;
  platform_admin_role: string;
  created_at: string;
}

interface SearchResult {
  user_id: string;
  business_email: string;
  legal_first_name: string;
  legal_last_name: string;
  role: string;
  organization_id: string;
  is_active: boolean;
}

export function AdminManagement() {
  const { data: adminInfo } = usePlatformAdmin();
  
  // State for grant flow
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<SearchResult | null>(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [justification, setJustification] = useState('');

  // State for revoke flow
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);

  // Get current user
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setCurrentUserId(user.id);
    });
  }, []);

  // Permission check
  if (!adminInfo?.permissions?.can_grant_admin_roles) {
    return (
      <Card>
        <CardContent className="py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You do not have permission to manage platform admins.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Fetch current admins
  const { data: admins, isLoading, error, refetch } = useQuery({
    queryKey: ['platform-admins'],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_all_platform_admins');
      
      if (error) throw error;
      return (data || []) as Admin[];
    }
  });

  // Search for users to grant admin to
  const { data: searchResults } = useQuery({
    queryKey: ['user-search', searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      
      const { data, error } = await supabase.rpc('search_users_for_platform_admin', {
        search_query: searchQuery
      });
      
      if (error) throw error;
      
      // Filter out users who are already platform admins
      const allUsers = (data as SearchResult[]) || [];
      const { data: adminUsers } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('is_platform_admin', true);
      
      const adminUserIds = new Set((adminUsers || []).map(u => u.user_id));
      return allUsers.filter(user => !adminUserIds.has(user.user_id));
    },
    enabled: searchQuery.length >= 2
  });

  // Grant admin mutation
  const grantAdminMutation = useMutation({
    mutationFn: async ({ 
      userId, 
      role, 
      justification 
    }: { 
      userId: string; 
      role: string; 
      justification: string;
    }) => {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          is_platform_admin: true,
          platform_admin_role: role as 'super_admin' | 'admin_manager' | 'admin_consultant',
          platform_admin_granted_at: new Date().toISOString(),
          platform_admin_granted_by: currentUserId
        })
        .eq('user_id', userId);
      
      if (updateError) throw updateError;

      await supabase.rpc('log_platform_admin_action', {
        p_action_type: 'grant_admin',
        p_target_type: 'user',
        p_target_id: userId,
        p_justification: justification,
        p_metadata: { role }
      });
    },
    onSuccess: () => {
      toast.success('Admin role granted successfully');
      setShowGrantDialog(false);
      setSelectedUser(null);
      setSelectedRole('');
      setJustification('');
      setSearchQuery('');
      refetch();
    },
    onError: (error: any) => {
      toast.error(`Failed to grant admin role: ${error.message}`);
    }
  });

  // Revoke admin mutation
  const revokeAdminMutation = useMutation({
    mutationFn: async ({ 
      userId 
    }: { 
      userId: string;
    }) => {
      // Single RPC call handles everything (update + log)
      const { data, error } = await (supabase.rpc as any)('revoke_platform_admin_role', {
        p_target_user_id: userId
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Admin role revoked successfully');
      setShowRevokeDialog(false);
      setSelectedAdmin(null);
      refetch();
    },
    onError: (error: any) => {
      logger.error('Revoke error:', error);
      toast.error(`Failed to revoke admin role: ${error.message}`);
    }
  });

  const isJustificationValid = justification.trim().length >= 200;
  const canSubmitGrant = selectedUser && selectedRole && isJustificationValid;

  const handleRevokeClick = (admin: Admin) => {
    setSelectedAdmin(admin);
    setShowRevokeDialog(true);
  };

  const handleConfirmRevoke = () => {
    if (selectedAdmin) {
      revokeAdminMutation.mutate({
        userId: selectedAdmin.user_id
      });
    }
  };

  const handleConfirmGrant = () => {
    if (selectedUser && selectedRole) {
      grantAdminMutation.mutate({
        userId: selectedUser.user_id,
        role: selectedRole,
        justification
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Admin Management</CardTitle>
            <CardDescription>
              Grant or revoke platform admin roles
            </CardDescription>
          </div>
          <Button onClick={() => setShowGrantDialog(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Grant Admin Role
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="text-center py-8 text-muted-foreground">
            Loading admins...
          </div>
        )}
        
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load admins: {(error as Error).message}
            </AlertDescription>
          </Alert>
        )}
        
        {!isLoading && !error && admins && admins.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No platform admins found
          </div>
        )}
        
        {!isLoading && !error && admins && admins.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Granted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((admin) => {
                const isCurrentUser = admin.user_id === currentUserId;
                return (
                  <TableRow key={admin.user_id}>
                    <TableCell>
                      {admin.legal_first_name && admin.legal_last_name
                        ? `${admin.legal_first_name} ${admin.legal_last_name}`
                        : 'N/A'}
                    </TableCell>
                    <TableCell>{admin.business_email}</TableCell>
                    <TableCell>
                      <Badge variant={
                        admin.platform_admin_role === 'super_admin' ? 'default' :
                        admin.platform_admin_role === 'admin_manager' ? 'secondary' :
                        'outline'
                      }>
                        {admin.platform_admin_role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(admin.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {isCurrentUser ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled
                              >
                                <UserMinus className="h-4 w-4 mr-2" />
                                Revoke
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Cannot revoke your own admin status
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRevokeClick(admin)}
                        >
                          <UserMinus className="h-4 w-4 mr-2" />
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        
        {/* Grant Admin Dialog */}
        <AlertDialog open={showGrantDialog} onOpenChange={setShowGrantDialog}>
          <AlertDialogContent className="max-w-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Grant Admin Role</AlertDialogTitle>
              <AlertDialogDescription>
                Search for a user and grant them platform admin privileges
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="space-y-4 py-4">
              {/* User Search */}
              <div className="space-y-2">
                <Label htmlFor="search">Search Users</Label>
                <Input
                  id="search"
                  placeholder="Search by name or email (min 2 characters)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchResults && searchResults.length > 0 && (
                  <div className="border rounded-md mt-2 max-h-48 overflow-y-auto">
                    {searchResults.map((user) => (
                      <div
                        key={user.user_id}
                        className={`p-3 cursor-pointer hover:bg-accent ${
                          selectedUser?.user_id === user.user_id ? 'bg-accent' : ''
                        }`}
                        onClick={() => setSelectedUser(user)}
                      >
                        <div className="font-medium">
                          {user.legal_first_name} {user.legal_last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user.business_email}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {searchQuery.length >= 2 && searchResults && searchResults.length === 0 && (
                  <div className="text-sm text-muted-foreground py-2">
                    No users found
                  </div>
                )}
              </div>

              {/* Selected User */}
              {selectedUser && (
                <Alert>
                  <AlertDescription>
                    Selected: {selectedUser.legal_first_name} {selectedUser.legal_last_name} ({selectedUser.business_email})
                  </AlertDescription>
                </Alert>
              )}

              {/* Role Selection */}
              <div className="space-y-2">
                <Label htmlFor="role">Admin Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin_manager">Admin Manager</SelectItem>
                    <SelectItem value="admin_consultant">Admin Consultant</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Justification */}
              <div className="space-y-2">
                <Label htmlFor="justification">
                  Justification (minimum 200 characters)
                </Label>
                <Textarea
                  id="justification"
                  placeholder="Provide detailed justification for granting admin role..."
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={4}
                />
                <div className="text-sm text-muted-foreground">
                  {justification.length} / 200 characters
                  {justification.length < 200 && ` (${200 - justification.length} more required)`}
                </div>
              </div>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={!canSubmitGrant || grantAdminMutation.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirmGrant();
                }}
              >
                {grantAdminMutation.isPending ? 'Granting...' : 'Grant Admin Role'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Revoke Admin Dialog */}
        <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke Admin Role</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to revoke admin privileges from{' '}
                {selectedAdmin?.legal_first_name} {selectedAdmin?.legal_last_name}?
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmRevoke}
                disabled={revokeAdminMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {revokeAdminMutation.isPending ? 'Revoking...' : 'Revoke Admin'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
